import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, filter, debounce } from "lodash";
import { Button, DataTable } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import "./parcel-order-list.scss";
import { Spin, Tooltip } from "antd";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useOrderListingStates } from "Helpers/index";
import { TFunction } from "i18next";
import { TooltipPlacement } from "antd/lib/tooltip";

interface ParcelOrderListProps {
  isCheckBoxShown: boolean;
  columns: any[];
  additionalComponent?: React.ReactNode;
  filterItems?: string[];
  radioButtonComponent?: React.ReactNode;
  archivedOrdersList?: any[];
  searchPlaceholder?: string;
  handleRefreshData: () => void;
  disableRowSelection?: (record: any) => boolean;
}

interface HeaderProps extends Partial<ParcelOrderListProps> {
  isLoading: boolean;
  handleSearch: (text: string) => void;
  handleFilterSelection: (selection: any) => void;
  filterSelection: any[];
  selectedRowCount: number;
  t: TFunction;
}

const Header = ({
  radioButtonComponent,
  handleRefreshData,
  isLoading,
  searchPlaceholder,
  handleSearch,
  handleFilterSelection,
  filterSelection,
  additionalComponent,
  isCheckBoxShown,
  selectedRowCount,
  t,
}: HeaderProps) => {
  const RefreshButton = ({ position }: { position: TooltipPlacement }) => {
    return (
      <Tooltip title="Refresh" placement={position}>
        <Button
          icon={isLoading ? <LoadingOutlined /> : <ReloadOutlined />}
          isDisabled={isLoading}
          onClick={handleRefreshData}
          shape="circle"
        />
      </Tooltip>
    );
  };

  return (
    <div className="po-header">
      {/* Radio Button Section */}
      {radioButtonComponent && (
        <div className="po-radio-btn-container">
          {radioButtonComponent}
          <RefreshButton position="right" />
        </div>
      )}

      {/* Filter Section */}
      <div className="filter-component">
        <DataTableFilter
          searchPlaceholder={
            searchPlaceholder || t("label.searchParcelOrderList")
          }
          onSearch={handleSearch}
          onFilter={handleFilterSelection}
          renderFilterSelection={filterSelection}
        />

        <div className="po-buttons-container">
          {!radioButtonComponent && <RefreshButton position="left" />}
          {additionalComponent}
        </div>
      </div>

      {/* Selection Counter */}
      {isCheckBoxShown && (
        <div className="selected-count">
          <h4>
            {t("label.totalSelection")}: {selectedRowCount}
          </h4>
        </div>
      )}
    </div>
  );
};

const ParcelOrderList: React.FC<ParcelOrderListProps> = ({
  isCheckBoxShown,
  columns,
  additionalComponent,
  filterItems,
  radioButtonComponent,
  searchPlaceholder,
  handleRefreshData,
  archivedOrdersList,
  disableRowSelection,
}) => {
  const { t } = useTranslation();
  const {
    allOrderList,
    selectedRows,
    setSelectedRows,
    customersList,
    expandable,
    locationList,
    isLoading,
    filterObj,
    setFilterObj,
    initialFilters,
    globalFilterState,
  } = useOrderListingStates();
  const [selectedRowCount, setSelectedRowCount] = useState<number>(
    selectedRows.length,
  );

  const filterItemsSelection = useMemo(
    () => [
      {
        key: "deliveryType",
        name: "deliveryType",
        label: t("placeholder.filterByType"),
        placeholder: t("placeholder.filterByType"),
        value: filterObj?.deliveryType,
        options: [
          { label: t("option.all"), value: "" },
          {
            label: t("option.FIRST_MILE_MID_MILE"),
            value: "First-Mile + Mid-Mile",
          },
          { label: t("option.END_TO_END"), value: "End To End" },
          { label: t("option.MID_MILE_ONLY"), value: "Mid-Mile Only" },
          {
            label: t("option.MID_MILE_LAST_MILE"),
            value: "Mid-Mile + Last-Mile",
          },
        ],
      },
      {
        key: "customersList",
        name: "customersList",
        label: t("placeholder.filterCustomer"),
        placeholder: t("placeholder.filterCustomer"),
        value: filterObj?.customersList,
        options: [{ label: t("option.all"), value: "" }].concat(
          customersList.map((org) => {
            return {
              label: org.name || "",
              value: org._id,
            };
          }),
        ),
      },
      {
        key: "locationID",
        name: "locationID",
        label: t("placeholder.filterLocation"),
        placeholder: t("placeholder.filterLocation"),
        value: filterObj?.locationID,
        options: [{ label: t("option.all"), value: "" }].concat(
          locationList?.map((location) => {
            return {
              label:
                location.primary == true
                  ? "Primary Address"
                  : location.locationName,
              value: location._id,
            };
          }),
        ),
      },
      {
        type: "date",
        key: "deliveryDate",
        name: "deliveryDate",
        label: t("placeholder.filterByDeliveryDate"),
        placeholder: t("placeholder.filterByDeliveryDate"),
        value: filterObj?.deliveryDate,
      },
    ],
    [filterItems],
  );

  const [filterSelection, setFilterSelection] = useState<any>([]);

  const setFiltersByItems = () => {
    if (filterItems) {
      let requiredFilterSelection = filter(
        filterItemsSelection,
        (filterObj) => {
          if (filterItems.includes(filterObj.key)) {
            return filterObj;
          }
        },
      );
      setFilterSelection(requiredFilterSelection);
    }
  };

  const rowSelection = {
    // don't remove selectedRowKeys, the 2nd param from onChange contains the entire row object
    // selectedRowKeys on the other hand contains only the unique rowKey from DataTable
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record: any) => ({
      disabled: disableRowSelection ? disableRowSelection(record) : false,
    }),
  };

  const debouncedSetFilterObj = useCallback(
    debounce((text: string) => {
      setFilterObj((prevFilter) => ({ ...prevFilter, orFields: text || null }));
    }, 400),
    [],
  );

  const handleSearch = (text: string) => {
    debouncedSetFilterObj(text);
  };

  const handleFilterSelection = (selection) => {
    setFilterObj((prevFilter) => ({
      ...prevFilter,
      customersList: selection.customersList,
      deliveryType: selection.deliveryType,
      locationID: selection.locationID,
      deliveryDate: selection.deliveryDate,
    }));
    setSelectedRows([]);
  };

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      setFilterObj(initialFilters);
    }
  }, [globalFilterState]);

  useEffect(() => {
    setFiltersByItems();
  }, [filterItems]);

  useEffect(() => {
    setSelectedRowCount(selectedRows.length);
  }, [selectedRows]);

  return (
    <div className="parcel-order-list">
      <Header
        radioButtonComponent={radioButtonComponent}
        handleRefreshData={handleRefreshData}
        isLoading={isLoading}
        searchPlaceholder={searchPlaceholder}
        handleSearch={handleSearch}
        handleFilterSelection={handleFilterSelection}
        filterSelection={filterSelection}
        additionalComponent={additionalComponent}
        isCheckBoxShown={isCheckBoxShown}
        selectedRowCount={selectedRowCount}
        t={t}
      />
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <DataTable
          scroll={{ x: true }}
          rowKey="_id"
          rowSelection={isCheckBoxShown ? rowSelection : undefined}
          columns={columns}
          dataSource={archivedOrdersList || allOrderList}
          expandable={expandable}
        />
      )}
    </div>
  );
};

export default ParcelOrderList;
